import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Pin } from 'lucide-react';
import { Card, CardBody, Button, Input, Textarea, TagChip } from '../components/ui';
import { LinkifiedText } from '../components/LinkifiedText';
import { DiscussionTopicFooter } from '../components/DiscussionTopicFooter';
import {
  createDraftPollQuestion,
  DiscussionPollComposer,
  draftPollIsValid,
  normalizeDraftPollQuestions,
  type DraftPollQuestion
} from '../components/DiscussionPollComposer';
import { AttachmentPicker } from '../components/Attachments';
import { useReplyEmailNotificationPrompt } from '../components/ReplyEmailNotificationPrompt';
import { uploadDiscussionAttachments } from '../lib/attachments';
import {
  countDiscussionTopics,
  createTopic,
  listDiscussionTopicsPage,
  listTags,
  setTopicReplyEmailNotifications,
  type DiscussionTopicsPageCursor
} from '../lib/firestore';
import { trackEvent } from '../lib/analytics';
import {
  groupDiscussionTags,
  resolveDiscussionTopicTags
} from '../lib/discussionCategories';
import { useMemberDirectory } from '../lib/memberDirectory';
import { formatPersonName } from '../lib/names';
import type { DiscussionTopic, Tag } from '../lib/types';
import { useAuth } from '../auth/AuthProvider';

const TOPICS_PER_PAGE = 10;

function topicActivityMs(topic: DiscussionTopic) {
  return Number(topic.lastActivityAt || topic.updatedAt || topic.createdAt || 0);
}

function CategoryRows(props: {
  rows: Tag[][];
  selectedIds: string[];
  onToggle: (tag: Tag) => void;
  includeAll?: boolean;
  allSelected?: boolean;
  onAll?: () => void;
}) {
  return (
    <div className="space-y-2">
      {props.rows.map((row, index) => (
        <div key={index} className="flex flex-wrap gap-2">
          {index === 0 && props.includeAll ? (
            <TagChip label="All" selected={props.allSelected} onClick={props.onAll} />
          ) : null}
          {row.map((tag) => (
            <TagChip
              key={tag.id}
              label={tag.name}
              selected={props.selectedIds.includes(tag.id)}
              onClick={() => props.onToggle(tag)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function paginationItems(currentPageIndex: number, pageCount: number) {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index);
  }

  const pages = new Set([0, pageCount - 1, currentPageIndex - 1, currentPageIndex, currentPageIndex + 1]);
  const sortedPages = Array.from(pages)
    .filter((page) => page >= 0 && page < pageCount)
    .sort((a, b) => a - b);
  const items: Array<number | 'ellipsis'> = [];

  sortedPages.forEach((page, index) => {
    const previous = sortedPages[index - 1];
    if (index > 0 && page - previous > 1) items.push('ellipsis');
    items.push(page);
  });

  return items;
}

export default function DiscussionPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const memberDirectory = useMemberDirectory();
  const [topics, setTopics] = React.useState<DiscussionTopic[]>([]);
  const [topicsLoaded, setTopicsLoaded] = React.useState(false);
  const [topicsLoading, setTopicsLoading] = React.useState(false);
  const [topicsError, setTopicsError] = React.useState<string | null>(null);
  const [topicsTotalCount, setTopicsTotalCount] = React.useState<number | null>(null);
  const [topicsCountLoading, setTopicsCountLoading] = React.useState(false);
  const [pageIndex, setPageIndex] = React.useState(0);
  const [pageCursors, setPageCursors] = React.useState<DiscussionTopicsPageCursor[]>([null]);
  const [nextPageCursor, setNextPageCursor] = React.useState<DiscussionTopicsPageCursor>(null);
  const [hasNextPage, setHasNextPage] = React.useState(false);
  const [tags, setTags] = React.useState<Tag[]>([]);

  // MULTI TAG FILTER (top chips)
  const [selectedFilterTagIds, setSelectedFilterTagIds] = React.useState<string[]>([]);
  const [filterDrawerOpen, setFilterDrawerOpen] = React.useState(false);

  const [isComposerOpen, setIsComposerOpen] = React.useState(false);
  const [title, setTitle] = React.useState('');
  const [body, setBody] = React.useState('');
  const [topicTagIds, setTopicTagIds] = React.useState<string[]>([]);
  const [pollQuestions, setPollQuestions] = React.useState<DraftPollQuestion[]>([]);
  const [attachmentFiles, setAttachmentFiles] = React.useState<File[]>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [composerError, setComposerError] = React.useState<string | null>(null);
  const { askReplyEmailNotification, replyEmailNotificationPrompt } = useReplyEmailNotificationPrompt();
  const pageLoadRef = React.useRef(0);
  const countLoadRef = React.useRef(0);
  const resetStateRef = React.useRef<unknown>(null);

  React.useEffect(() => {
    listTags('discussion', true).then(setTags).catch(() => setTags([]));
  }, []);

  const loadTopicCount = React.useCallback(async (tagIds: string[]) => {
    const requestId = countLoadRef.current + 1;
    countLoadRef.current = requestId;
    setTopicsCountLoading(true);

    try {
      const count = await countDiscussionTopics({ tagIds });
      if (countLoadRef.current !== requestId) return;
      setTopicsTotalCount(count);
    } catch (error) {
      if (countLoadRef.current !== requestId) return;
      console.error(error);
      setTopicsTotalCount(null);
    } finally {
      if (countLoadRef.current === requestId) setTopicsCountLoading(false);
    }
  }, []);

  const loadTopicPage = React.useCallback(
    async (nextPageIndex: number, cursor: DiscussionTopicsPageCursor, tagIds: string[]) => {
      const requestId = pageLoadRef.current + 1;
      pageLoadRef.current = requestId;
      setTopicsLoading(true);
      setTopicsError(null);

      try {
        const result = await listDiscussionTopicsPage({
          tagIds,
          cursor,
          pageSize: TOPICS_PER_PAGE,
          includePinned: nextPageIndex === 0
        });
        if (pageLoadRef.current !== requestId) return;
        setTopics(result.items);
        setPageIndex(nextPageIndex);
        setNextPageCursor(result.nextCursor);
        setHasNextPage(result.hasMore);
        setTopicsLoaded(true);
      } catch (error) {
        if (pageLoadRef.current !== requestId) return;
        console.error(error);
        setTopics([]);
        setNextPageCursor(null);
        setHasNextPage(false);
        setTopicsLoaded(true);
        setTopicsError('Topics could not be loaded.');
      } finally {
        if (pageLoadRef.current === requestId) setTopicsLoading(false);
      }
    },
    []
  );

  const loadTopicPageSequence = React.useCallback(
    async (targetPageIndex: number, tagIds: string[]) => {
      const requestId = pageLoadRef.current + 1;
      pageLoadRef.current = requestId;
      setTopicsLoading(true);
      setTopicsError(null);

      const maxTargetPageIndex =
        topicsTotalCount === null
          ? targetPageIndex
          : Math.max(0, Math.min(targetPageIndex, Math.ceil(topicsTotalCount / TOPICS_PER_PAGE) - 1));
      const nextCursors = [...pageCursors];
      let startPageIndex = Math.min(maxTargetPageIndex, nextCursors.length - 1);
      while (startPageIndex > 0 && nextCursors[startPageIndex] === undefined) startPageIndex -= 1;

      let result: Awaited<ReturnType<typeof listDiscussionTopicsPage>> | null = null;

      try {
        for (let index = startPageIndex; index <= maxTargetPageIndex; index += 1) {
          const cursor = nextCursors[index] ?? null;
          result = await listDiscussionTopicsPage({
            tagIds,
            cursor,
            pageSize: TOPICS_PER_PAGE,
            includePinned: index === 0
          });
          if (pageLoadRef.current !== requestId) return;
          nextCursors[index + 1] = result.nextCursor;
        }

        if (!result) return;
        setTopics(result.items);
        setPageIndex(maxTargetPageIndex);
        setPageCursors(nextCursors);
        setNextPageCursor(result.nextCursor);
        setHasNextPage(result.hasMore);
        setTopicsLoaded(true);
      } catch (error) {
        if (pageLoadRef.current !== requestId) return;
        console.error(error);
        setTopics([]);
        setNextPageCursor(null);
        setHasNextPage(false);
        setTopicsLoaded(true);
        setTopicsError('Topics could not be loaded.');
      } finally {
        if (pageLoadRef.current === requestId) setTopicsLoading(false);
      }
    },
    [pageCursors, topicsTotalCount]
  );

  React.useEffect(() => {
    setTopics([]);
    setTopicsLoaded(false);
    setPageIndex(0);
    setPageCursors([null]);
    setNextPageCursor(null);
    setHasNextPage(false);
    setTopicsTotalCount(null);
    void loadTopicCount(selectedFilterTagIds);
    void loadTopicPage(0, null, selectedFilterTagIds);
  }, [loadTopicCount, loadTopicPage, selectedFilterTagIds]);

  React.useEffect(() => {
    const resetAt = (loc.state as { resetDiscussionsAt?: unknown } | null)?.resetDiscussionsAt;
    if (!resetAt || resetStateRef.current === resetAt) return;
    resetStateRef.current = resetAt;

    setFilterDrawerOpen(false);
    setIsComposerOpen(false);
    if (selectedFilterTagIds.length) {
      setSelectedFilterTagIds([]);
      return;
    }

    setTopics([]);
    setTopicsLoaded(false);
    setPageIndex(0);
    setPageCursors([null]);
    setNextPageCursor(null);
    setHasNextPage(false);
    setTopicsTotalCount(null);
    void loadTopicCount([]);
    void loadTopicPage(0, null, []);
  }, [loadTopicCount, loadTopicPage, loc.state, selectedFilterTagIds.length]);

  const visibleTopics = React.useMemo(() => {
    return [...topics].sort((a, b) => {
      const aPinned = Boolean(a.pinned);
      const bPinned = Boolean(b.pinned);
      if (aPinned !== bPinned) return aPinned ? -1 : 1;
      return topicActivityMs(b) - topicActivityMs(a);
    });
  }, [topics]);

  const categoryRows = React.useMemo(() => groupDiscussionTags(tags), [tags]);
  const pageCount = React.useMemo(() => {
    if (topicsTotalCount === null) return null;
    return Math.max(1, Math.ceil(topicsTotalCount / TOPICS_PER_PAGE));
  }, [topicsTotalCount]);
  const pageItems = React.useMemo(() => paginationItems(pageIndex, pageCount ?? 1), [pageCount, pageIndex]);

  function toggleFilterTag(tag: Tag) {
    const selected = selectedFilterTagIds.includes(tag.id);
    setSelectedFilterTagIds((prev) => (selected ? prev.filter((x) => x !== tag.id) : [...prev, tag.id]));
  }

  function scrollToTopOnMobile() {
    if (window.matchMedia('(max-width: 639px)').matches) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }

  function goNextPage() {
    if (topicsLoading || !hasNextPage || !nextPageCursor) return;
    scrollToTopOnMobile();
    const nextIndex = pageIndex + 1;
    setPageCursors((current) => {
      const next = [...current];
      next[nextIndex] = nextPageCursor;
      return next;
    });
    void loadTopicPage(nextIndex, nextPageCursor, selectedFilterTagIds);
  }

  function goPreviousPage() {
    if (topicsLoading || pageIndex === 0) return;
    scrollToTopOnMobile();
    const nextIndex = Math.max(0, pageIndex - 1);
    void loadTopicPage(nextIndex, pageCursors[nextIndex] ?? null, selectedFilterTagIds);
  }

  function goToPage(targetPageIndex: number) {
    if (topicsLoading || targetPageIndex === pageIndex) return;
    scrollToTopOnMobile();
    if (targetPageIndex === 0) {
      setPageCursors([null]);
      void loadTopicPage(0, null, selectedFilterTagIds);
      return;
    }

    const knownCursor = pageCursors[targetPageIndex];
    if (knownCursor !== undefined) {
      void loadTopicPage(targetPageIndex, knownCursor, selectedFilterTagIds);
      return;
    }

    void loadTopicPageSequence(targetPageIndex, selectedFilterTagIds);
  }

  function toggleTopicTag(tag: Tag) {
    const selected = topicTagIds.includes(tag.id);
    setTopicTagIds((prev) => (selected ? prev.filter((x) => x !== tag.id) : [...prev, tag.id]));
  }

  async function submit() {
    if (!title.trim() || !body.trim() || !user?.sub) return;
    if (submitting) return;
    if (!topicTagIds.length) {
      setComposerError('Select at least one category.');
      return;
    }
    if (pollQuestions.length && !draftPollIsValid(pollQuestions)) {
      setComposerError('Complete every poll question with at least two options, or remove the poll.');
      return;
    }

    const trimmedTitle = title.trim();
    const trimmedBody = body.trim();
    const normalizedPollQuestions = pollQuestions.length ? normalizeDraftPollQuestions(pollQuestions) : [];
    setSubmitting(true);
    setComposerError(null);
    try {
      const id = await createTopic({
        title: trimmedTitle,
        body: trimmedBody,
        authorProfileId: user.sub,
        authorDisplayName: formatPersonName(user.displayName),
        tagIds: topicTagIds,
        pollQuestions: normalizedPollQuestions.length ? normalizedPollQuestions : undefined
      });
      if (attachmentFiles.length) {
        await uploadDiscussionAttachments({
          target: { kind: 'topic', topicId: id },
          ownerProfileId: user.sub,
          files: attachmentFiles
        });
      }
      await trackEvent('create_topic', {
        topic_id: id,
        tag_count: topicTagIds.length,
        poll_question_count: normalizedPollQuestions.length,
        attachment_count: attachmentFiles.length,
        title_length: trimmedTitle.length,
        body_length: trimmedBody.length
      });
      const wantsReplyEmails = await askReplyEmailNotification();
      await setTopicReplyEmailNotifications(user.sub, id, wantsReplyEmails).catch((notificationError) => {
        console.error(notificationError);
      });
      setTitle('');
      setBody('');
      setTopicTagIds([]);
      setPollQuestions([]);
      setAttachmentFiles([]);
      setIsComposerOpen(false);
      window.location.assign(`/discussion/${id}`);
    } catch (e: any) {
      console.error(e);
      setComposerError(e?.message ? String(e.message) : 'Failed to post topic');
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = Boolean(
    title.trim() &&
      body.trim() &&
      topicTagIds.length > 0 &&
      (!pollQuestions.length || draftPollIsValid(pollQuestions)) &&
      !submitting
  );

  const pagination = topicsLoaded && (visibleTopics.length > 0 || pageIndex > 0 || hasNextPage) ? (
    <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-zinc-600">
      <span>
        {topicsLoading
          ? 'Loading page...'
          : `Page ${pageIndex + 1}${pageCount ? ` of ${pageCount}` : ''}${
              topicsTotalCount === null
                ? topicsCountLoading
                  ? ' · counting topics'
                  : ''
                : ` · ${topicsTotalCount} topic${topicsTotalCount === 1 ? '' : 's'} total`
            }`}
      </span>
      <div className="flex flex-wrap items-center gap-1">
        <Button
          variant="outline"
          className="px-2 py-1 text-xs disabled:opacity-50"
          disabled={topicsLoading || pageIndex === 0}
          onClick={goPreviousPage}
        >
          Previous
        </Button>
        {pageCount && pageCount > 1 ? (
          <div className="flex flex-wrap items-center gap-1">
            {pageItems.map((item, index) =>
              item === 'ellipsis' ? (
                <span key={`ellipsis-${index}`} className="px-1 text-zinc-500">
                  ...
                </span>
              ) : (
                <Button
                  key={item}
                  variant={item === pageIndex ? 'secondary' : 'outline'}
                  className="h-7 min-w-7 px-2 py-1 text-xs disabled:opacity-50"
                  disabled={topicsLoading || item === pageIndex}
                  onClick={() => goToPage(item)}
                >
                  {item + 1}
                </Button>
              )
            )}
          </div>
        ) : null}
        <Button
          variant="outline"
          className="px-2 py-1 text-xs disabled:opacity-50"
          disabled={topicsLoading || !hasNextPage}
          onClick={goNextPage}
        >
          Next
        </Button>
      </div>
    </div>
  ) : null;

  return (
    <>
      {replyEmailNotificationPrompt}
      <div className="grid grid-cols-1 gap-4">
      <Card className="overflow-hidden">
        <div className="flex items-start justify-between gap-3 border-b border-zinc-300 bg-zinc-200 px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-zinc-900">Discussions</div>
          </div>
          <Button onClick={() => setIsComposerOpen((v) => !v)}>New topic</Button>
        </div>
        <CardBody className="space-y-3">
          {!isComposerOpen ? (
            <div className="space-y-2">
              <div className="hidden md:block">
                <CategoryRows
                  rows={categoryRows}
                  selectedIds={selectedFilterTagIds}
                  onToggle={toggleFilterTag}
                  includeAll
                  allSelected={selectedFilterTagIds.length === 0}
                  onAll={() => setSelectedFilterTagIds([])}
                />
              </div>

              <div className="flex flex-wrap items-center gap-2 md:hidden">
                <TagChip
                  label="All"
                  selected={selectedFilterTagIds.length === 0}
                  onClick={() => setSelectedFilterTagIds([])}
                />
                <button
                  type="button"
                  onClick={() => setFilterDrawerOpen((current) => !current)}
                  className="inline-flex h-8 items-center gap-1.5 rounded-xl border border-zinc-300 bg-white px-3 text-xs font-medium text-zinc-800 transition hover:bg-zinc-50"
                  aria-expanded={filterDrawerOpen}
                  aria-label={filterDrawerOpen ? 'Hide category filters' : 'Show category filters'}
                  title={filterDrawerOpen ? 'Hide category filters' : 'Show category filters'}
                >
                  Categories
                  {selectedFilterTagIds.length ? (
                    <span className="rounded-full bg-zinc-900 px-1.5 py-0.5 text-[10px] leading-none text-white">
                      {selectedFilterTagIds.length}
                    </span>
                  ) : null}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${filterDrawerOpen ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                </button>
              </div>

              {filterDrawerOpen ? (
                <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-3 md:hidden">
                  <CategoryRows rows={categoryRows} selectedIds={selectedFilterTagIds} onToggle={toggleFilterTag} />
                </div>
              ) : null}
            </div>
          ) : null}

          {isComposerOpen ? (
            <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-3">
              <div className="grid grid-cols-1 gap-2">
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" />
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Write the post"
                  rows={5}
                />

                <div className="text-sm text-zinc-900">
                  Select relevant categories to post against - choosing at least one:
                </div>
                <div className="rounded-xl border border-zinc-200 bg-white p-3">
                  <CategoryRows rows={categoryRows} selectedIds={topicTagIds} onToggle={toggleTopicTag} />
                </div>

                {pollQuestions.length ? (
                  <DiscussionPollComposer questions={pollQuestions} onChange={setPollQuestions} />
                ) : null}

                {composerError ? (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-2 text-xs text-red-800">
                    {composerError}
                  </div>
                ) : null}

                <div className="flex flex-wrap items-center justify-end gap-2">
                  <AttachmentPicker files={attachmentFiles} onChange={setAttachmentFiles} disabled={submitting} />
                  {!pollQuestions.length ? (
                    <Button variant="outline" onClick={() => setPollQuestions([createDraftPollQuestion(0)])}>
                      Add poll
                    </Button>
                  ) : null}
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setIsComposerOpen(false);
                      setComposerError(null);
                      setAttachmentFiles([]);
                    }}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button onClick={submit} disabled={!canSubmit}>
                    {submitting ? 'Posting...' : 'Post'}
                  </Button>
                </div>
              </div>
            </div>
          ) : null}

          {topicsError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-2 text-sm text-red-800">
              {topicsError}
            </div>
          ) : null}

          {topicsLoading && !topicsLoaded ? <div className="text-sm text-zinc-600">Loading topics...</div> : null}

          {topicsLoaded && !topicsLoading && !topicsError && visibleTopics.length === 0 ? (
            <div className="text-sm text-zinc-600">No topics</div>
          ) : null}

          {pagination}

          {visibleTopics.map((t) => (
            (() => {
              const topicTags = resolveDiscussionTopicTags(t.tagIds, tags);
              const pinned = Boolean(t.pinned);
              return (
            <div
              key={t.id}
              role="link"
              tabIndex={0}
              onClick={() => nav(`/discussion/${t.id}`)}
              onKeyDown={(evt) => {
                if (evt.key === 'Enter' || evt.key === ' ') {
                  evt.preventDefault();
                  nav(`/discussion/${t.id}`);
                }
              }}
              className="relative cursor-pointer rounded-xl border border-zinc-200 p-3 hover:bg-zinc-50"
            >
              <div className="flex items-start justify-between gap-3">
                <div className={['min-w-0 flex-1', pinned ? 'pr-8 sm:pr-0' : ''].join(' ')}>
                  <div className="break-words text-sm font-semibold">{t.title}</div>
                  {topicTags.length ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {topicTags.map((tag) => (
                        <span
                          key={tag.id}
                          className="rounded-full border border-zinc-300 bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700"
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
                <div className={['flex shrink-0 items-center gap-2', pinned ? 'pr-8 sm:pr-0' : ''].join(' ')}>
                  {t.hasPoll ? (
                    <span className="rounded-full border border-zinc-300 bg-white px-2 py-0.5 text-xs font-medium text-zinc-700">
                      Poll
                    </span>
                  ) : null}
                  {pinned ? (
                    <span
                      className="absolute right-3 top-3 inline-flex h-7 w-7 items-center justify-center rounded-full border border-zinc-300 bg-zinc-100 text-zinc-700 sm:static"
                      title="Pinned discussion"
                      aria-label="Pinned discussion"
                    >
                      <Pin className="h-3.5 w-3.5" aria-hidden />
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="mt-2 line-clamp-2 text-sm text-zinc-700">
                <LinkifiedText text={t.body} />
              </div>

              <DiscussionTopicFooter topic={t} directory={memberDirectory} />
            </div>
              );
            })()
          ))}
          {pagination ? (
            <div
              className="sm:hidden"
              role="navigation"
              aria-label="Topic pagination at bottom of list"
            >
              {pagination}
            </div>
          ) : null}
        </CardBody>
      </Card>
      </div>
    </>
  );
}
